import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {NgbActiveModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import { interval, Subscription } from 'rxjs';
import { CalmeanDataService } from 'src/app/core/services/calmean-data.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import {FormsModule} from "@angular/forms";
import {DatePipe, NgClass, NgForOf, NgSwitch, NgSwitchCase, NgSwitchDefault} from "@angular/common";

interface SMSMessage {
  type?: string;
  status?: string;
  sender?: string;
  content?: string;
  createDate?: any;
  statusDate?: any;
}

@Component({
    selector: 'app-sms-gw',
    templateUrl: './sms-gw.component.html',
    imports: [
        FormsModule,
        NgSwitch,
        NgForOf,
        NgClass,
        DatePipe,
        NgSwitchCase,
        NgbPagination,
        NgSwitchDefault
    ],
    styleUrls: ['./sms-gw.component.scss']
})
export class SMSGwComponent implements OnInit, OnDestroy {
  // Inputs set by opener (SMSGwButtonComponent) — plan step 1
  @Input() deviceUUID: string | null = null;
  @Input() deviceId: string | null = null;
  @Input() msisdn: string | null = null;
  @Input() sender: string = '';

  // View model (plan step 1)
  hints: string[] = [];
  recipient: string = '';
  phoneBookNumberValid = true;
  blockSend = false;
  messageToSend = '';
  messages: SMSMessage[] = [];
  pagedMessages: SMSMessage[] = [];
  page = 1;
  itemsPerPage = 5;
  maxSize = 5;
  totalItems = 0;

  private refreshSub: Subscription | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private calmeanDataService: CalmeanDataService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.msisdn) {
      this.recipient = this.msisdn;
    }
    this.initialize();
    // Auto-refresh every 5 seconds (plan step 4)
    this.refreshSub = interval(5000).subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
      this.refreshSub = null;
    }
  }

  // Close modal (plan step 1)
  cancel(): void {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
      this.refreshSub = null;
    }
    this.activeModal.dismiss();
  }

  // Validate phone number (plan step 1)
  validatePhone(): void {
    if (!this.recipient) {
      this.phoneBookNumberValid = false;
      return;
    }
    if (String(this.recipient).length > 8 && this.deviceId) {
      this.calmeanDataService.validatePhoneNumber(this.deviceId, this.recipient, 'PL')
        .subscribe((resp: any) => {
          if (resp && resp.status === 'SUCCESS') {
            this.recipient = resp.msisdnE164;
            this.phoneBookNumberValid = true;
            this.refresh();
          } else {
            this.phoneBookNumberValid = false;
          }
        }, (err: any) => {
          this.notification.error('Błąd walidacji numeru: ' + JSON.stringify(err));
        });
    }
  }

  // Send SMS (plan step 1)
  sendMessage(): void {
    if (!this.messageToSend || this.messageToSend.length < 1) {
      this.notification.error('Brak wiadomości do wysłania.');
      return;
    }
    if (!this.phoneBookNumberValid) {
      this.notification.error('Błędny numer odbiorcy.');
      return;
    }
    this.blockSend = true;
    this.calmeanDataService.sendSMSGwMessage(this.recipient, this.messageToSend, this.sender)
      .subscribe((resp: any) => {
        if (resp && resp.status === 'SUCCESS') {
          this.notification.success('SMS Wysłany: ' + this.messageToSend + ' do: ' + this.recipient);
          this.refresh();
        } else {
          this.notification.error('Błąd wysyłania SMS.');
        }
        this.blockSend = false;
      }, (err: any) => {
        this.notification.error('Błąd wysyłania SMS: ' + JSON.stringify(err));
        this.blockSend = false;
      });
  }

  // Pagination helper (plan step 1)
  getDevicesWithFilterPage(): void {
    this.pagedMessages = this.messages.slice((this.page - 1) * this.itemsPerPage, this.page * this.itemsPerPage);
  }

  setText(message: string): void {
    this.messageToSend = message;
  }

  // Refresh message history (plan step 1)
  refresh(): void {
    if (this.recipient && this.recipient.length > 5) {
      this.calmeanDataService.getSMSGwData(this.recipient)
        .subscribe((resp: any) => {
          if (resp && resp.status === 'SUCCESS') {
            this.messages = resp.messages || [];
            this.totalItems = this.messages.length;
            this.getDevicesWithFilterPage();
            this.notification.success('Historia SMS odświeżona.');
          } else {
            this.notification.error('Błąd pobierania historii sms.');
          }
        }, (err: any) => {
          this.notification.error('Błąd pobierania historii sms: ' + JSON.stringify(err));
        });
    }
  }

  // Color mapping for badges (plan step 1)
  getColorByStatus(status?: string): string {
    if (status === 'DELIVERED') {
      return 'badge badge-success';
    }
    if (status === 'SENT') {
      return 'badge badge-secondary';
    }
    return 'badge badge-danger';
  }

  // Load hints (plan step 1)
  initialize(): void {
    if (this.sender === 'CALMEAN') {
      this.hints = [
        'Skorzystaj z kodu ANZA przy zamawianiu oryginalnego produktu CALMEAN na https://www.calmean.com/sklep/ i zaoszczedz 10 PLN! Spiesz sie, oferta ograniczona!',
        'Z kodem ANCE zamawiajac oryginalny smartwatch CALMEAN na https://www.calmean.com/sklep/ otrzymasz dedykowana Ladowarke GRATIS! Spiesz sie, oferta ograniczona!',
        'Uzyj kodu ANRI przy zamawianiu oryginalnego produktu CALMEAN na https://www.calmean.com/sklep/ i odbierz Phone Ring GRATIS! Spiesz sie, ilosc ograniczona!',
        'Wejdz na https://www.calmean.com/sklep/, wpisz kod ANLA przy zamawianiu oryginalnego smartwatcha CALMEAN i otrzymaj Ochrone na ekran GRATIS! Oferta ograniczona!',
        // ... add other default hints if needed ...
      ];
    } else {
      this.calmeanDataService.getSMSGwHints(this.deviceUUID)
        .subscribe((resp: any) => {
          if (resp && resp.status === 'SUCCESS') {
            this.hints = resp.hintMessages || [];
          } else {
            this.notification.error('Błąd pobierania danych.');
          }
        }, (err: any) => {
          this.notification.error('Błąd pobierania danych: ' + JSON.stringify(err));
        });
    }
      this.refresh();
  }
}
